import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  X,
  ArrowLeft,
  Check,
  ArrowRight,
  ImagePlus,
  CheckCircle2,
  Loader2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { QUOTE_EVENT } from "../context/QuoteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLEAN_OPTIONS = [
  "Gevel",
  "Dak",
  "Terras",
  "Gevel en Dak",
  "Gevel en Terras",
  "Dak en Terras",
  "Gevel, Dak en Terras",
  "Bedrijfspand",
];

const PROPERTY_TYPES = [
  "Tussenwoning",
  "Hoekwoning",
  "2-onder-1-kap",
  "Vrijstaande woning",
  "Bedrijfspand",
];

// Which screen belongs to which macro step (for the 3-dot progress)
const MACRO_OF_SCREEN = [0, 0, 1, 1, 2];
const TOTAL_SCREENS = 5;

const Radio = ({ selected, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex w-full items-center gap-3 rounded-full px-4 py-3 text-left transition-colors duration-200 ${
      selected ? "bg-navy/10" : "hover:bg-secondary"
    }`}
  >
    <span
      className={`grid h-7 w-7 shrink-0 place-items-center rounded-full transition-colors duration-200 ${
        selected ? "bg-navy text-white" : "bg-slate-200 text-transparent"
      }`}
    >
      <Check className="h-4 w-4" strokeWidth={3} />
    </span>
    <span className="font-display text-[15px] font-semibold text-ink">{label}</span>
  </button>
);

const Field = ({ label, children }) => (
  <div>
    <label className="font-display text-base font-bold text-ink">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

export const QuoteWizard = () => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const closeQuote = () => setOpen(false);
  const [screen, setScreen] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    clean: "",
    property: "",
    description: "",
    photos: [],
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    const handler = (e) => {
      setPreset(e.detail || null);
      setOpen(true);
    };
    window.addEventListener(QUOTE_EVENT, handler);
    return () => window.removeEventListener(QUOTE_EVENT, handler);
  }, []);

  useEffect(() => {
    if (open) {
      setScreen(0);
      setDone(false);
      setForm((f) => ({ ...f, clean: preset || "", photos: [], description: "" }));
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => (document.body.style.overflow = "");
  }, [open, preset]);

  const macro = MACRO_OF_SCREEN[screen];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        results.push({ path: data.path, filename: data.filename });
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...results] }));
      toast.success(`${results.length} foto('s) geüpload`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Uploaden mislukt. Probeer een kleinere afbeelding (max 10MB)."
      );
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const canNext = () => {
    if (screen === 0) return !!form.clean;
    if (screen === 1) return !!form.property;
    if (screen === 4)
      return form.firstName.trim() && form.email.trim() && form.phone.trim();
    return true;
  };

  const next = () => setScreen((s) => Math.min(s + 1, TOTAL_SCREENS - 1));
  const back = () => setScreen((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      const message =
        `Wat reinigen: ${form.clean}\n` +
        `Type pand: ${form.property || "-"}\n` +
        `Omschrijving: ${form.description || "-"}` +
        (form.photos.length
          ? `\nFoto's bijgevoegd door klant: ${form.photos.length}`
          : "");
      await axios.post(`${API}/contact`, {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        service: form.clean,
        message,
        photos: form.photos,
      });
      setDone(true);
      toast.success("Bedankt! We nemen snel contact met je op.");
    } catch (e) {
      toast.error("Er ging iets mis. Probeer het opnieuw of bel ons.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3.5 text-ink placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-navy";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-ink/70 backdrop-blur-sm p-3 sm:p-6"
          onClick={closeQuote}
          data-testid="quote-wizard"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex max-h-[86vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
          >
            {/* Header / progress */}
            <div className="flex shrink-0 items-center gap-4 border-b border-slate-100 px-6 py-4 sm:px-8">
              <div className="flex flex-1 items-center">
                {[0, 1, 2].map((m, i) => (
                  <div key={m} className="flex flex-1 items-center last:flex-none">
                    <span
                      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold transition-colors duration-300 ${
                        macro > m
                          ? "bg-navy text-white"
                          : macro === m
                          ? "bg-navy text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {macro > m ? <Check className="h-4 w-4" strokeWidth={3} /> : m + 1}
                    </span>
                    {i < 2 && (
                      <span
                        className={`mx-2 h-0.5 flex-1 rounded ${
                          macro > m ? "bg-navy" : "bg-slate-200"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={closeQuote}
                aria-label="Sluiten"
                data-testid="quote-close"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-secondary hover:text-ink"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            {done ? (
              <div className="flex flex-col items-center px-6 py-12 text-center" data-testid="quote-success">
                <CheckCircle2 className="h-16 w-16 text-teal" />
                <h3 className="mt-6 font-display text-xl font-bold text-ink">
                  Aanvraag verzonden!
                </h3>
                <p className="mt-3 max-w-sm text-muted-foreground">
                  Bedankt voor je aanvraag. Je ontvangt nog dezelfde dag een
                  persoonlijke, vrijblijvende offerte op maat.
                </p>
                <button
                  onClick={closeQuote}
                  data-testid="quote-finish"
                  className="mt-8 rounded-full bg-navy px-8 py-3.5 font-semibold text-white transition-transform hover:-translate-y-0.5"
                >
                  Sluiten
                </button>
              </div>
            ) : (
              <>
                <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 sm:px-7">
                  {screen > 0 && (
                    <button
                      onClick={back}
                      data-testid="quote-back"
                      className="mb-4 grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-ink transition-colors hover:bg-secondary"
                      aria-label="Terug"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}

                  {screen === 0 && (
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink">
                        Wat wilt u laten reinigen?
                      </h3>
                      <div className="mt-6 space-y-1">
                        {CLEAN_OPTIONS.map((o) => (
                          <Radio
                            key={o}
                            label={o}
                            selected={form.clean === o}
                            onClick={() => set("clean", o)}
                            testid={`quote-clean-${o.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {screen === 1 && (
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink">
                        Wat voor type pand heeft u?
                      </h3>
                      <div className="mt-6 space-y-1">
                        {PROPERTY_TYPES.map((o) => (
                          <Radio
                            key={o}
                            label={o}
                            selected={form.property === o}
                            onClick={() => set("property", o)}
                            testid={`quote-property-${o.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {screen === 2 && (
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink">
                        Voeg meer informatie toe
                      </h3>
                      <p className="mt-1 text-muted-foreground">
                        Beschrijf uw type dak, gevel of terras.
                      </p>
                      <textarea
                        rows={5}
                        maxLength={250}
                        value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        placeholder="Bijv. een houten terras en keramische dakpannen."
                        data-testid="quote-description"
                        className={`mt-6 resize-none ${inputCls}`}
                      />
                      <p className="mt-2 text-right text-xs text-slate-400">
                        {form.description.length}/250 tekens
                      </p>
                    </div>
                  )}

                  {screen === 3 && (
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink">
                        Voeg een foto toe
                      </h3>
                      <p className="mt-1 text-muted-foreground">
                        Voeg eventueel een foto toe van jouw situatie.
                      </p>
                      <label className="mt-6 flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 px-6 py-8 text-center transition-colors hover:border-navy hover:bg-secondary/50">
                        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-navy/10 text-navy">
                          {uploading ? (
                            <Loader2 className="h-6 w-6 animate-spin" />
                          ) : (
                            <ImagePlus className="h-6 w-6" />
                          )}
                        </span>
                        <span className="font-semibold text-ink">
                          {uploading ? "Bezig met uploaden..." : "Foto's selecteren"}
                        </span>
                        <span className="text-xs text-slate-400">
                          Optioneel · JPG, PNG of WEBP · max 10MB
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          disabled={uploading}
                          className="hidden"
                          data-testid="quote-photos"
                          onChange={(e) => {
                            uploadFiles(e.target.files);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {form.photos.length > 0 && (
                        <div className="mt-4 space-y-2" data-testid="quote-photos-count">
                          <p className="text-sm font-medium text-teal">
                            {form.photos.length} foto('s) geüpload
                          </p>
                          <div className="space-y-2">
                            {form.photos.map((p, i) => (
                              <div
                                key={p.path}
                                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-2.5"
                              >
                                <span className="flex items-center gap-2 min-w-0 text-sm text-ink">
                                  <CheckCircle2 className="h-4 w-4 shrink-0 text-teal" />
                                  <span className="truncate">{p.filename || "Foto"}</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removePhoto(i)}
                                  data-testid={`quote-photo-remove-${i}`}
                                  className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                                  aria-label="Foto verwijderen"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {screen === 4 && (
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink">
                        Vul je gegevens in
                      </h3>
                      <div className="mt-6 space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Voornaam">
                            <input
                              value={form.firstName}
                              onChange={(e) => set("firstName", e.target.value)}
                              placeholder="Voornaam"
                              data-testid="quote-firstname"
                              className={inputCls}
                            />
                          </Field>
                          <Field label="Achternaam">
                            <input
                              value={form.lastName}
                              onChange={(e) => set("lastName", e.target.value)}
                              placeholder="Achternaam"
                              data-testid="quote-lastname"
                              className={inputCls}
                            />
                          </Field>
                        </div>
                        <Field label="E-mailadres">
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="Bijv. email@example.com"
                            data-testid="quote-email"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Telefoonnummer">
                          <div className="flex items-center overflow-hidden rounded-2xl border-2 border-slate-200 focus-within:border-navy">
                            <span className="px-4 text-sm font-semibold text-slate-500 border-r-2 border-slate-200 py-3.5">
                              NL +31
                            </span>
                            <input
                              type="tel"
                              value={form.phone}
                              onChange={(e) => set("phone", e.target.value)}
                              placeholder="Vul je telefoonnummer in"
                              data-testid="quote-phone"
                              className="w-full px-4 py-3.5 outline-none text-ink placeholder:text-slate-400"
                            />
                          </div>
                        </Field>
                      </div>
                      <p className="mt-5 text-center text-xs text-slate-400 leading-relaxed">
                        Door je aanvraag te versturen ga je akkoord met onze
                        algemene voorwaarden en ons privacybeleid.
                      </p>
                    </div>
                  )}

                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-slate-100 px-6 py-4 sm:px-7">
                    {screen === 3 && form.photos.length === 0 && !uploading && (
                      <button
                        onClick={next}
                        data-testid="quote-skip"
                        className="mb-3 w-full rounded-full border-2 border-slate-200 px-6 py-3.5 font-semibold text-ink transition-colors hover:bg-secondary"
                      >
                        Overslaan
                      </button>
                    )}
                    {screen < 4 ? (
                      <button
                        onClick={next}
                        disabled={!canNext() || uploading}
                        data-testid="quote-next"
                        className="group flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                      >
                        Volgende
                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={!canNext() || loading}
                        data-testid="quote-submit"
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3.5 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" /> Versturen...
                          </>
                        ) : (
                          "Offerte aanvragen"
                        )}
                      </button>
                    )}
                    <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                      <Clock className="h-4 w-4" /> In 2 minuten klaar · gratis &
                      vrijblijvend
                    </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteWizard;
